import { useState } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  downloadComplianceReport,
  listComplianceReports,
  type ComplianceReportResponse,
} from '../api/complianceReports.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate, formatPeriod } from '../lib/format.js';

/**
 * /compliance-reports — list of auto-generated regulator filing artefacts.
 *
 * One report per approved payroll run. Server-side filtering by period +
 * country (the query key carries the filter so React Query refetches on
 * change). Each row downloads the CSV or links to the metadata detail page.
 */
export const complianceReportsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/compliance-reports',
  component: ComplianceReportsListPage,
});

// A small fixed window of years for the filter. Anchored on the current year
// so the dropdown stays relevant without server round-trips for distinct
// periods (v1 has at most a handful of reports per org).
function recentYears(): number[] {
  const current = new Date().getFullYear();
  return [current + 1, current, current - 1, current - 2];
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const COUNTRIES = ['CD', 'NG', 'GH'];

function ComplianceReportsListPage(): JSX.Element {
  const [periodYear, setPeriodYear] = useState<number | ''>('');
  const [periodMonth, setPeriodMonth] = useState<number | ''>('');
  const [countryCode, setCountryCode] = useState<string>('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    // Filter values are part of the key → changing a filter refetches.
    queryKey: ['compliance-reports', 'list', periodYear, periodMonth, countryCode],
    queryFn: () =>
      listComplianceReports({
        periodYear: periodYear === '' ? undefined : periodYear,
        periodMonth: periodMonth === '' ? undefined : periodMonth,
        countryCode: countryCode === '' ? undefined : countryCode,
      }),
  });

  const reports = data?.reports ?? [];

  return (
    <section className="p-8 space-y-6">
      <header>
        <h1 className="font-serif text-3xl">Compliance reports</h1>
        <p className="text-sm text-gray-400">
          Auto-generated regulator filing artefacts. One report per approved payroll run.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Year"
          value={periodYear === '' ? '' : String(periodYear)}
          onChange={(v) => setPeriodYear(v === '' ? '' : Number(v))}
          options={[
            { value: '', label: 'All years' },
            ...recentYears().map((y) => ({ value: String(y), label: String(y) })),
          ]}
        />
        <FilterSelect
          label="Month"
          value={periodMonth === '' ? '' : String(periodMonth)}
          onChange={(v) => setPeriodMonth(v === '' ? '' : Number(v))}
          options={[
            { value: '', label: 'All months' },
            ...MONTHS.map((m) => ({ value: String(m.value), label: m.label })),
          ]}
        />
        <FilterSelect
          label="Country"
          value={countryCode}
          onChange={setCountryCode}
          options={[
            { value: '', label: 'All countries' },
            ...COUNTRIES.map((c) => ({ value: c, label: c })),
          ]}
        />
      </div>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
      {data && reports.length === 0 ? <EmptyState /> : null}
      {data && reports.length > 0 ? <ReportsTable reports={reports} /> : null}
    </section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-400">
      {label}
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ReportsTable({ reports }: { reports: ComplianceReportResponse[] }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Period</th>
            <th className="px-4 py-3 font-medium">Country</th>
            <th className="px-4 py-3 font-medium">Format</th>
            <th className="px-4 py-3 font-medium">Generated</th>
            <th className="px-4 py-3 font-medium">Payroll run</th>
            <th className="px-4 py-3 font-medium text-right">Rows</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id} className="border-t border-gray-900 hover:bg-gray-950">
              <td className="px-4 py-3 text-white">{formatPeriod(r.periodYear, r.periodMonth)}</td>
              <td className="px-4 py-3 text-gray-400">{r.countryCode}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-400">{r.regulatorFormat}</td>
              <td className="px-4 py-3 text-gray-400">{formatDate(r.generatedAt)}</td>
              <td className="px-4 py-3">
                <Link
                  to="/payroll-runs/$id"
                  params={{ id: r.payrollRunId }}
                  className="font-mono text-xs text-gray-300 hover:underline"
                >
                  {r.payrollRunId}
                </Link>
              </td>
              <td className="px-4 py-3 text-right font-mono text-xs text-gray-300">{r.rowCount}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <DownloadButton id={r.id} />
                  <Link
                    to="/compliance-reports/$id"
                    params={{ id: r.id }}
                    className="text-xs border border-gray-700 rounded px-2 py-1 text-gray-300 hover:bg-gray-900"
                  >
                    Details
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Per-row download button. Shows a transient busy state and surfaces an
 * inline error if the request fails. Mirrors the payslip PDF button.
 */
export function DownloadButton({ id }: { id: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="flex items-center gap-2">
      {err ? (
        <span className="text-xs text-red-400" role="alert" title={err}>
          Failed
        </span>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          try {
            await downloadComplianceReport(id);
          } catch (caught) {
            setErr(caught instanceof ApiError ? caught.message : 'Download failed. Try again.');
          } finally {
            setBusy(false);
          }
        }}
        className="text-xs border border-gray-700 rounded px-2 py-1 text-gray-300 hover:bg-gray-900 disabled:opacity-50"
        aria-label={`Download CSV for report ${id}`}
      >
        {busy ? '…' : 'Download'}
      </button>
    </div>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading compliance reports...
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-dashed border-gray-800 p-10 text-center space-y-3">
      <h2 className="font-medium text-white">No compliance reports yet</h2>
      <p className="text-sm text-gray-400">
        No compliance reports yet. Approve a CD payroll run to generate the first one.
      </p>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }): JSX.Element {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <Alert
      variant="error"
      title="Could not load compliance reports"
      action={
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Retry
        </button>
      }
    >
      {message}
    </Alert>
  );
}
